export type TypographyConfig = {
  [key: string]: {
    fontSize: string;
    fontWeight?: string;
    fontStyle?: string;
  };
};
export function generateTypographyClasses(typography: TypographyConfig) {
  if (!typography) return;
  if (typeof document === "undefined") {
    console.warn(
      "Cannot generate typography classes: document is not available"
    );
    return;
  }

  // Utility function to convert px to rem
  const convertToRem = (px: string): string => {
    const baseSize = 16; // Assuming base font-size is 16px
    const value = parseFloat(px);
    if (isNaN(value)) {
      console.warn(`Invalid fontSize: ${px}, defaulting to 1rem`);
      return "1rem";
    }
    return `${value / baseSize}rem`;
  };

  // Function to sanitize class/element names
  const sanitizeSelector = (name: string): string =>
    name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

  // Function to generate a CSS rule for font-size, font-weight, and font-style
  const generateClass = (
    selector: string,
    config: { fontSize: string; fontWeight?: string; fontStyle?: string }
  ) => {
    const { fontSize, fontWeight } = config;
    const remFontSize = convertToRem(fontSize);
    const minSize = parseFloat(remFontSize) * 0.5;
    const maxSize = parseFloat(remFontSize) * 1;
    const clampFontSize = `clamp(${minSize}rem, ${
      parseFloat(remFontSize) * 1.3
    }vw + ${minSize}rem, ${maxSize}rem)`;

    // Handle fontWeight and fontStyle separately
    let fontWeightClass = "";
    let fontStyleClass = "";

    if (fontWeight) {
      const weightMatch = fontWeight.match(/^(\d+)(italic|oblique)?$/);
      if (weightMatch) {
        fontWeightClass = `font-weight: ${weightMatch[1]};`;
        fontStyleClass = weightMatch[2] ? `font-style: ${weightMatch[2]};` : "";
      } else {
        fontWeightClass = `font-weight: ${fontWeight};`;
      }
    }

    return `${selector} { font-size: ${clampFontSize}; ${fontWeightClass} ${fontStyleClass} }`;
  };

  // Generate Tailwind-like classes and element selectors
  const generateHeadingClasses = () => {
    const classes: string[] = [];

    Object.keys(typography).forEach((key) => {
      const sanitizedKey = sanitizeSelector(key);

      // Generate .class
      const classSelector = `.${sanitizedKey}`;
      const classContent = generateClass(classSelector, typography[key]);
      classes.push(classContent);

      // Generate element selector
      const elementSelector = sanitizedKey;
      const elementContent = generateClass(elementSelector, typography[key]);
      classes.push(elementContent);
    });

    // Special: Make <p> inherit "Body Font"
    const bodyFontConfig = typography["Body Font"];
    if (bodyFontConfig) {
      const pContent = generateClass("body", bodyFontConfig);
      classes.push(pContent);
    } else {
      console.warn(`Body Font not found in typography config`);
    }

    // Insert into <style> tag
    let styleElement = document.querySelector(
      "style#typography-styles"
    ) as HTMLStyleElement;
    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = "typography-styles";
      document.head.appendChild(styleElement);
    }
    styleElement.innerHTML = classes.join("\n");
  };

  generateHeadingClasses();
}
